import React, {
  useMemo,
  useContext,
}                               from 'react';
import styled                   from 'styled-components';
import * as Yup                 from 'yup';
import { Formik, FormikConfig } from 'formik';
import { ISelectOption }        from '@typings/components';
import {
  ILogo,
  ICreditUnion,
  INoteValues,
  ISalesFee,
  ISalesPersonFees,
  INote,
  IAccountFormValues,
  IFeeValues,
}                               from '@typings/accountManagement';
import { EAppPage }             from '@enums/common';
import {
  EAccountStatus,
  EAccountPage,
}                               from '@enums/accountManagement';
import { FormikRefContext }     from '@utils/FormikRefContext';
import {
  LinkButton,
  LinksWrapper,
}                               from '@components/index';
import { FullNoteModal }        from '@modules/accountManagement/pages/FullNoteModal';
import { AccountInfoForm }      from '../components/AccountInfoForm';

const Wrapper = styled.div`
  padding: 20px 30px 60px;

  @media (min-width: 1280px) {
    padding: 20px 100px 60px;
  }
`;

const salespersonFeeSchema: Yup.Schema<ISalesFee> = Yup.object().shape({
  type          : Yup.number().required(),
  feeUserType   : Yup.number().required(),
  salesPersonId : Yup.string(),
  percentage    : Yup.number()
    .min(0, 'Minimum 0 percentage')
    .max(100, 'Maximum 100 percentage')
    .when('salesPersonId', {
      is        : (salesPersonId) => !!salesPersonId,
      then      : Yup.number().required(),
      otherwise : Yup.number().nullable().notRequired(),
    }),
});

const salespersonFeeListSchema: Yup.Schema<ISalesFee[]> = Yup.array().of(salespersonFeeSchema);

const validationSchema: Yup.Schema<IAccountFormValues> = Yup.object().shape({
  cuid                         : Yup.string().required(),
  alias                        : Yup.string(),
  name                         : Yup.string().required(),
  title                        : Yup.string(),
  contactName                  : Yup.string().required(),
  phoneNumber                  : Yup.string().required(),
  phoneExtension               : Yup.string(),
  email                        : Yup.string().email().required(),
  address1                     : Yup.string().required(),
  address2                     : Yup.string(),
  city                         : Yup.string().required(),
  state                        : Yup.string().required(),
  zip                          : Yup.string(),
  status                       : Yup.number().required(),
  defaultLocationId            : Yup.string().nullable().notRequired(),
  useDefaultLocationForInvoice : Yup.boolean(),
  logo                         : Yup.mixed().required(),
  invoiceLocation              : Yup.object().shape({
    locationName : Yup.string(),
    email        : Yup.string().max(150),
    address1     : Yup.string(),
    address2     : Yup.string(),
    city         : Yup.string(),
    state        : Yup.string(),
    zip          : Yup.string(),
  }),
  achBillingInformation: Yup.object().shape({
    bankName                   : Yup.string(),
    routingNumber              : Yup.string(),
    accountNumber              : Yup.string(),
    encodedAuthorizationNumber : Yup.string(),
  }),
  pricing         : Yup.mixed().required(),
  salesPersonFees : Yup.object().shape({
    monthlyFees               : salespersonFeeListSchema,
    personalAdministrationFee : salespersonFeeListSchema,
    dealerAdministrationFee   : salespersonFeeListSchema,
    nadaMonthlyFees           : salespersonFeeListSchema,
    nadaBookOutFees           : salespersonFeeListSchema,
    expressRecoveryFees       : salespersonFeeListSchema,
    deficiencyTrackingFees    : salespersonFeeListSchema,
    vrmFees                   : salespersonFeeListSchema,
    srsFees                   : salespersonFeeListSchema,
    reindeerFees              : salespersonFeeListSchema,
  }),
});

const getInitialValues = (
  accountData     : ICreditUnion | null,
  accountLogo     : ILogo | null,
  nextCuid        : string | null,
  defaultPricing  : IFeeValues[],
  salesPersonFees : ISalesPersonFees,
): IAccountFormValues => ({
  cuid                         : accountData?.cuid ?? nextCuid ?? '',
  alias                        : accountData?.alias ?? '',
  name                         : accountData?.name ?? '',
  contactName                  : accountData?.defaultContactPerson.contactName ?? '',
  title                        : accountData?.title ?? '',
  phoneNumber                  : accountData?.defaultContactPerson.phoneNumber ?? '',
  phoneExtension               : accountData?.defaultContactPerson.phoneExtension ?? '',
  email                        : accountData?.defaultContactPerson.email ?? '',
  address1                     : accountData?.address1 ?? '',
  address2                     : accountData?.address2 ?? '',
  city                         : accountData?.city ?? '',
  state                        : accountData?.state ?? '',
  zip                          : accountData?.zip ?? '',
  status                       : accountData?.status ?? EAccountStatus.Active,
  defaultLocationId            : accountData?.defaultLocationId ?? null,
  logo                         : accountLogo ?? null,
  useDefaultLocationForInvoice : accountData?.useDefaultLocationForInvoice ?? false,
  invoiceLocation              : {
    locationName : accountData?.invoiceLocation?.locationName ?? '',
    email        : accountData?.invoiceContactPerson?.email ?? '',
    address1     : accountData?.invoiceLocation?.address1 ?? '',
    address2     : accountData?.invoiceLocation?.address2 ?? '',
    city         : accountData?.invoiceLocation?.city ?? '',
    state        : accountData?.invoiceLocation?.state ?? '',
    zip          : accountData?.invoiceLocation?.zip ?? '',
  },
  achBillingInformation: {
    bankName                   : accountData?.achBillingInformation?.bankName ?? '',
    routingNumber              : accountData?.achBillingInformation?.routingNumber ?? '',
    accountNumber              : accountData?.achBillingInformation?.accountNumber ?? '',
    encodedAuthorizationNumber :
      accountData?.achBillingInformation?.encodedAuthorizationNumber ?? '',
  },
  pricing: accountData?.pricing ?? defaultPricing ?? [],
  salesPersonFees,
});

interface IEditAccountPageProps {
  push                    : (path: string) => void;
  accountData             : ICreditUnion | null;
  accountLogo             : ILogo | null;
  defaultPricing          : IFeeValues[];
  states                  : ISelectOption[];
  nextCuid?               : string;
  clientStatuses          : ISelectOption[];
  handleSubmitAccountForm : FormikConfig<IAccountFormValues>['onSubmit'];
  salespersonList         : ISelectOption[];
  isLoadingSalespersons   : boolean;
  salesPersonsFeeList     : ISalesPersonFees;
  notes                   : INote[];
  onAddNewNote            : FormikConfig<INoteValues>['onSubmit'];
  handleToggleNoteModal   : (note: INote | null) => void;
  isOpenNoteModal         : boolean;
  noteToView              : INote | null;
  addSalesPersonRow       : (
    blockName     : keyof ISalesPersonFees,
    data          : ISalesFee[],
    setFieldValue : (field: string, value: ISalesFee[], shouldValidate?: boolean) => void,
  ) => void;
  removeSalesPersonRow    : (
    blockName     : keyof ISalesPersonFees,
    index         : number,
    data          : ISalesPersonFees,
    setFieldValue : (field: string, value: ISalesFee[], shouldValidate?: boolean) => void,
  ) => void;
}

export const AccountInfoPage: React.FC<IEditAccountPageProps> = ({
  push,
  states,
  nextCuid = null,
  accountData,
  accountLogo,
  defaultPricing,
  clientStatuses,
  handleSubmitAccountForm,
  salespersonList,
  isLoadingSalespersons,
  addSalesPersonRow,
  removeSalesPersonRow,
  salesPersonsFeeList,
  notes,
  onAddNewNote,
  handleToggleNoteModal,
  isOpenNoteModal,
  noteToView,
}) => {
  const formikRef = useContext(FormikRefContext);

  const initialValues = useMemo<IAccountFormValues>(
    () => getInitialValues(accountData, accountLogo, nextCuid, defaultPricing, salesPersonsFeeList),
    [accountData, accountLogo, nextCuid, defaultPricing, salesPersonsFeeList],
  );

  return (
    <Wrapper>
      <LinksWrapper>
        <LinkButton
          label   = "Return to Account Management"
          onClick = {(): void => push(`${EAppPage.AccountManagement}/${EAccountPage.Search}`)}
        />
      </LinksWrapper>

      <FullNoteModal
        handleClose = {handleToggleNoteModal}
        isOpen      = {isOpenNoteModal}
        noteToView  = {noteToView}
      />

      <Formik
        innerRef         = {formikRef}
        onSubmit         = {handleSubmitAccountForm}
        initialValues    = {initialValues}
        validationSchema = {validationSchema}
        validateOnChange
        enableReinitialize
      >
        <AccountInfoForm
          id                    = {accountData?.id}
          states                = {states}
          isNewAccount          = {!accountData?.id}
          clientStatuses        = {clientStatuses}
          addSalesPersonRow     = {addSalesPersonRow}
          salespersonList       = {salespersonList}
          removeSalesPersonRow  = {removeSalesPersonRow}
          isLoadingSalespersons = {isLoadingSalespersons}
          notes                 = {notes}
          onAddNewNote          = {onAddNewNote}
          handleToggleNoteModal = {handleToggleNoteModal}
        />
      </Formik>
    </Wrapper>
  );
};
